import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  TextField,
  Button,
  IconButton,
} from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import { useSnackbar } from 'notistack'
import React from 'react'
import type { JSX } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { updateDeviceImei } from '@/api/Device'
import type { Device } from '@/pages/Device/Device'
import { labelSx } from '@/styles/MuiComponentStyles'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />
})

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  device?: Device
}

type FormValues = {
  IMEIVerification: string
  newIMEI: string
}

const ImeiChangeDialog = ({ device, open, setOpen }: Props): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('common')

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: { IMEIVerification: '', newIMEI: '' },
  })

  const onClose = () => setOpen(false)

  const onSubmit = async (values: FormValues) => {
    const res = await updateDeviceImei(
      device?.uid,
      values.IMEIVerification,
      values.newIMEI,
    )

    if (res.status === 200) {
      enqueueSnackbar(t('device.info.imeiChange.imeiChangeSuccess'), {
        variant: 'success',
      })
    } else {
      enqueueSnackbar(t('device.info.imeiChange.imeiChangeFailed'), {
        variant: 'error',
      })
    }

    reset()
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      slots={{ transition: Transition }}
      slotProps={{ transition: { direction: 'down' } }}
      onClose={onClose}
      keepMounted
      aria-labelledby="imei-change-title"
    >
      <DialogTitle
        id="imei-change-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontWeight: 'normal', fontSize: '1.0rem' }}>
          {t('general.areYouSure')}
        </span>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <div
          style={{
            fontWeight: 'normal',
            fontSize: '1.0rem',
            marginBottom: '16px',
          }}
        >
          {t('device.info.imeiChange.header')}
        </div>
        <div>
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Old IMEI verification */}
              <Controller
                name="IMEIVerification"
                control={control}
                rules={{
                  required: t(
                    'device.info.imeiChange.oldImeiInputLengthValidationError',
                  ),
                  pattern: {
                    value: /^\d{4}$/,
                    message: t(
                      'device.info.imeiChange.oldImeiInputLengthValidationError',
                    ),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    label={t('device.info.imeiChange.oldImeiInputLabel')}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      inputLabel: { sx: labelSx },
                      input: {
                        inputProps: {
                          maxLength: 4,
                          inputMode: 'numeric',
                          pattern: String.raw`\d{4}`,
                        },
                      },
                    }}
                    onInput={(e) => {
                      const input = e.target as HTMLInputElement
                      input.value = input.value
                        .replaceAll(/\D/g, '')
                        .slice(0, 4)
                      field.onChange(input.value)
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* New IMEI */}
              <Controller
                name="newIMEI"
                control={control}
                rules={{
                  required: t(
                    'device.info.imeiChange.newImeiInputLengthValidationError',
                  ),
                  pattern: {
                    value: /^\d{15}$/,
                    message: t(
                      'device.info.imeiChange.newImeiInputLengthValidationError',
                    ),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    label={t('device.info.imeiChange.newImeiInputLabel')}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      inputLabel: { sx: labelSx },
                      input: {
                        inputProps: {
                          maxLength: 15,
                          inputMode: 'numeric',
                          pattern: String.raw`\d{15}`,
                        },
                      },
                    }}
                    onInput={(e) => {
                      const input = e.target as HTMLInputElement
                      input.value = input.value
                        .replaceAll(/\D/g, '')
                        .slice(0, 15)
                      field.onChange(input.value)
                    }}
                  />
                )}
              />

              <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    reset()
                    onClose()
                  }}
                >
                  {t('general.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={!isValid}
                >
                  {t('general.yes')}
                </Button>
              </DialogActions>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImeiChangeDialog
